/**
 * <seasonal-popup>
 * ---------------------------------------------------------------------------
 * Reusable seasonal / event popup (content rendered in Liquid). Pushes Google
 * Tag Manager dataLayer events (view / cta_click / dismiss) for campaign KPIs.
 *
 * dataLayer events (when `tracking-enabled` and not in QA-bypass / editor):
 *  - popup_view     fired when the popup is displayed
 *  - popup_click    fired when the CTA link is clicked
 *  - popup_dismiss  fired when the popup is closed
 *  Each payload carries: popup_name. popup_click also adds popup_link_url
 *  and popup_link_text.
 *
 * Behaviors
 *  - Delay timer : `desktop-delay` seconds on desktop, `mobile-delay` on mobile.
 *  - Alt trigger : show on the Nth page view in the current session
 *                  (`pageviews-trigger`, 0 disables).
 *  - Once-per-session gate (sessionStorage) + multi-day snooze (localStorage).
 *  - Coexistence with <newsletter-popup>:
 *      * subscriber / already-seen newsletter → start timer immediately
 *      * newsletter still pending            → wait for it to close, then timer
 *  - Editor preview : shows immediately in the theme editor.
 *
 * Storage keys
 *  - sessionStorage  'theme:seasonal-popup-shown'   boolean flag (once per session)
 *  - sessionStorage  'theme:seasonal-pageviews'     integer counter for pageviews
 *  - localStorage    'theme:seasonal-popup-seen-at' ms timestamp of last display
 *  - (read-only) localStorage 'theme:popup-filled', 'theme:popup-appeared'
 *    are written by the existing newsletter popup; we read them to coordinate.
 */
(function () {
  'use strict';

  if (window.customElements.get('seasonal-popup')) return;

  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
  const HTML_OPEN_CLASS = 'seasonal-popup-open';
  const URL_BYPASS_PARAM = 'seasonal-popup';
  const NEWSLETTER_WAIT_TIMEOUT_MS = 10 * 60 * 1000;
  const PAGEVIEW_TRIGGER_DELAY_MS = 1500;

  const EVENTS = {
    open: 'seasonal-popup:open',
    close: 'seasonal-popup:close'
  };

  // Google Tag Manager dataLayer event names (the GTM trigger contract).
  const DATALAYER_EVENTS = {
    view: 'popup_view',
    ctaClick: 'popup_click',
    dismiss: 'popup_dismiss'
  };

  const SELECTORS = {
    close: '[data-seasonal-close]',
    cta: '[data-seasonal-cta]',
    closeButton: '.seasonal-popup__close',
    newsletter: 'newsletter-popup',
    newsletterOpen: 'newsletter-popup[open]'
  };

  const STORAGE_KEYS = {
    sessionShown: 'theme:seasonal-popup-shown',
    sessionPageviews: 'theme:seasonal-pageviews',
    localSeenAt: 'theme:seasonal-popup-seen-at',
    newsletterFilled: 'theme:popup-filled',
    newsletterAppeared: 'theme:popup-appeared'
  };

  // Safe storage helpers (some contexts block access to storage)
  const safeGet = (storage, key) => {
    try { return storage.getItem(key); } catch (e) { return null; }
  };
  const safeSet = (storage, key, value) => {
    try { storage.setItem(key, value); } catch (e) { /* ignore */ }
  };

  class SeasonalPopup extends HTMLElement {
    #showTimer = null;
    #pendingObserver = null;
    #abortController = null;
    #previouslyFocused = null;
    #isOpen = false;
    #isForced = false;
    #trackingAllowed = false;

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      // Detect URL-param QA bypass (?seasonal-popup=1).
      this.#isForced = this.hasAttribute('force-show') || this.#hasUrlBypass();
      
      // Bind close triggers on data-seasonal-close elements
      this.querySelectorAll(SELECTORS.close).forEach((el) => {
        el.addEventListener('click', this.#onCloseClick, { signal });
      });

      // Keyboard: ESC to close
      this.addEventListener('keydown', this.#onKeydown, { signal });

      // Decide whether dataLayer pushes are allowed (never in QA-bypass or editor preview).
      this.#trackingAllowed = this.trackingEnabled
        && !this.#isForced
        && !this.hasAttribute('editor-preview');

        this.#trackingAllowed =true;

      // CTA click: push tracking, mark as seen, then let the link navigate.
      const cta = this.querySelector(SELECTORS.cta);
      if (cta) cta.addEventListener('click', this.#onCtaClick, { signal });

      // Editor preview or URL bypass: show immediately, bypass all gating
      if (this.hasAttribute('editor-preview') || this.#isForced) {
        requestAnimationFrame(() => this.#open());
        return;
      }

      // Increment pageview counter (this page counts as a view)
      const pv = (parseInt(safeGet(sessionStorage, STORAGE_KEYS.sessionPageviews) || '0', 10) || 0) + 1;
      safeSet(sessionStorage, STORAGE_KEYS.sessionPageviews, String(pv));

      // Gating: once per session + N-day snooze
      if (!this.#isEligibleToShow()) return;

      // Alt trigger: if pageview threshold met, show sooner (after a brief settle)
      if (this.pageviewsTrigger > 0 && pv >= this.pageviewsTrigger) {
        this.#scheduleShow(PAGEVIEW_TRIGGER_DELAY_MS);
        return;
      }

      // Coexistence with newsletter popup
      if (this.coexistWithNewsletter && this.#isNewsletterPending()) {
        this.#waitForNewsletterClose(() => this.#scheduleShow(this.delayMs));
      } else {
        this.#scheduleShow(this.delayMs);
      }
    }

    disconnectedCallback() {
      if (this.#showTimer) clearTimeout(this.#showTimer);
      if (this.#pendingObserver) this.#pendingObserver.disconnect();
      if (this.#abortController) this.#abortController.abort();
    }

    // -----------------------------------------------------------------------
    // Attribute-backed getters
    // -----------------------------------------------------------------------
    get desktopDelayMs() {
      return (parseInt(this.getAttribute('desktop-delay') || '30', 10) || 30) * 1000;
    }
    get mobileDelayMs() {
      return (parseInt(this.getAttribute('mobile-delay') || '50', 10) || 50) * 1000;
    }
    get delayMs() {
      return this.#isMobile() ? this.mobileDelayMs : this.desktopDelayMs;
    }
    get pageviewsTrigger() {
      return parseInt(this.getAttribute('pageviews-trigger') || '0', 10) || 0;
    }
    get hideDays() {
      return parseInt(this.getAttribute('hide-days') || '3', 10) || 0;
    }
    get oncePerSession() {
      return this.hasAttribute('once-per-session');
    }
    get coexistWithNewsletter() {
      return this.hasAttribute('coexist-newsletter');
    }
    get trackingEnabled() {
      return this.hasAttribute('tracking-enabled');
    }
    get trackingId() {
      return this.getAttribute('tracking-id') || '';
    }

    // -----------------------------------------------------------------------
    // Eligibility
    // -----------------------------------------------------------------------
    #isMobile() {
      return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    }

    #isEligibleToShow() {
      // Once per session
      if (this.oncePerSession && safeGet(sessionStorage, STORAGE_KEYS.sessionShown) === 'true') {
        return false;
      }
      // Snooze window (N days since last display)
      const seenAt = parseInt(safeGet(localStorage, STORAGE_KEYS.localSeenAt) || '0', 10);
      if (seenAt && this.hideDays > 0) {
        const ageMs = Date.now() - seenAt;
        const snoozeMs = this.hideDays * 24 * 60 * 60 * 1000;
        if (ageMs < snoozeMs) return false;
      }
      return true;
    }

    // -----------------------------------------------------------------------
    // Newsletter coexistence
    // -----------------------------------------------------------------------
    #isNewsletterPending() {
      // If the user already filled the newsletter, they're a subscriber → no wait
      if (safeGet(localStorage, STORAGE_KEYS.newsletterFilled) === 'true') return false;
      // If the newsletter popup already appeared in a prior session, no wait
      if (safeGet(localStorage, STORAGE_KEYS.newsletterAppeared) === 'true') return false;
      // Newsletter popup must exist in the DOM and be eligible to show
      return document.querySelector(SELECTORS.newsletter) !== null;
    }

    #waitForNewsletterClose(callback) {
      const nl = document.querySelector(SELECTORS.newsletter);
      if (!nl) { callback(); return; }

      let opened = false;
      this.#pendingObserver = new MutationObserver(() => {
        const hasOpen = nl.hasAttribute('open');
        if (hasOpen) opened = true;
        if (opened && !hasOpen) {
          this.#stopPendingObserver();
          callback();
        }
      });
      this.#pendingObserver.observe(nl, { attributes: true, attributeFilter: ['open'] });

      // Safety fallback: if newsletter never opens within the timeout, proceed anyway.
      setTimeout(() => {
        if (this.#pendingObserver) {
          this.#stopPendingObserver();
          callback();
        }
      }, NEWSLETTER_WAIT_TIMEOUT_MS);
    }

    #stopPendingObserver() {
      if (this.#pendingObserver) {
        this.#pendingObserver.disconnect();
        this.#pendingObserver = null;
      }
    }

    // -----------------------------------------------------------------------
    // Scheduling
    // -----------------------------------------------------------------------
    #scheduleShow(delayMs) {
      if (this.#showTimer) clearTimeout(this.#showTimer);
      this.#showTimer = setTimeout(() => this.#showIfStillEligible(), Math.max(0, delayMs));
    }

    #showIfStillEligible() {
      if (!this.#isEligibleToShow()) return;
      // Don't overlap: if newsletter is currently open, wait again
      if (document.querySelector(SELECTORS.newsletterOpen)) {
        this.#waitForNewsletterClose(() => this.#scheduleShow(this.delayMs));
        return;
      }
      this.#open();
    }

    // -----------------------------------------------------------------------
    // Open / close
    // -----------------------------------------------------------------------
    #open = () => {
      if (this.#isOpen) return;
      this.#isOpen = true;

      this.#previouslyFocused = document.activeElement;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add(HTML_OPEN_CLASS);

      // Focus the close button for accessibility (defer to let transition start)
      const closeBtn = this.querySelector(SELECTORS.closeButton);
      if (closeBtn) setTimeout(() => closeBtn.focus(), 50);

      // Emit event for analytics / other listeners
      this.dispatchEvent(new CustomEvent(EVENTS.open, { bubbles: true }));
      this.#pushDataLayer(DATALAYER_EVENTS.view);

      // Persist gating flags (skip in forced/QA mode so reloads keep showing it)
      if (!this.#isForced) this.#markAsSeen();
    };

    #close = () => {
      if (!this.#isOpen) return;
      this.#isOpen = false;

      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove(HTML_OPEN_CLASS);

      this.dispatchEvent(new CustomEvent(EVENTS.close, { bubbles: true }));
      this.#pushDataLayer(DATALAYER_EVENTS.dismiss);

      if (this.#previouslyFocused && typeof this.#previouslyFocused.focus === 'function') {
        try { this.#previouslyFocused.focus(); } catch (e) { /* ignore */ }
      }
    };

    #markAsSeen = () => {
      safeSet(sessionStorage, STORAGE_KEYS.sessionShown, 'true');
      safeSet(localStorage, STORAGE_KEYS.localSeenAt, String(Date.now()));
    };

    #hasUrlBypass() {
      try {
        return new URLSearchParams(window.location.search).get(URL_BYPASS_PARAM) === '1';
      } catch (e) {
        return false;
      }
    }

    // -----------------------------------------------------------------------
    // Tracking (Google Tag Manager dataLayer)
    // -----------------------------------------------------------------------
    #pushDataLayer(eventName, extra = {}) {
      
      if (!this.#trackingAllowed) return;
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(this.#buildTrackingPayload(eventName, extra));
      } catch (e) { /* ignore */ }
    }

    #buildTrackingPayload(eventName, extra) {
      return {
        event: eventName,
        popup_name: this.trackingId,
        ...extra
      };
    }

    // -----------------------------------------------------------------------
    // Event handlers
    // -----------------------------------------------------------------------
    #onCtaClick = (event) => {
      const link = event.currentTarget;
      this.#pushDataLayer(DATALAYER_EVENTS.ctaClick, {
        popup_link_url: link?.href || '',
        popup_link_text: link?.textContent.trim() || ''
      });
      if (!this.#isForced) this.#markAsSeen();
    };

    #onCloseClick = (event) => {
      event.preventDefault();
      this.#close();
    };

    #onKeydown = (event) => {
      if (event.key === 'Escape' && this.#isOpen) {
        event.preventDefault();
        this.#close();
      }
    };
  }

  window.customElements.define('seasonal-popup', SeasonalPopup);
})();
